import { Accordion, Button, Grid, Input, Text } from '@afterpaytouch/core'
import type { InputOnChange } from '@afterpaytouch/core'
import React, { FunctionComponent, ReactNode, useEffect, useId, useRef, useState } from 'react'

const GridItem = Grid.Item
const GridFixedItem = Grid.FixedItem
import { useUpdateCheckoutPromoMutation } from '@afterpaytouch/portal-api/consumer/checkout'
import { ErrorCode } from '@afterpaytouch/portal-api'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { promoMutationCacheKey, useAppRouter, useCheckoutState, useCheckoutToken } from '../../state'
import { Trans, useTranslation } from '../../utils/convergence'
import { useMoney } from '../Money/useMoney'
import styles from './style.module.css'

const MAX_ATTEMPTS = 5

const PROMO_SILENT_NO_OP_ERROR_CODE = 'promo_silent_no_op'

type ViewState =
  | { kind: 'collapsed' }
  | { kind: 'expanded'; inputValue: string; errorCode: string | null }
  | { kind: 'applied'; promoCode: string; discountAmount: string }

interface Props {
  testNameSpace?: string
}

function resolveErrorMessage(errorCode: string, t: (key: string) => string): string {
  switch (errorCode.trim()) {
    case ErrorCode.PromoCodeAssignError:
      return t('promoCode:errorInvalid')
    case ErrorCode.AlreadyAssigned:
      return t('promoCode:errorAlreadyUsed')
    case ErrorCode.RateLimited:
      return t('promoCode:tooManyAttempts')
    case PROMO_SILENT_NO_OP_ERROR_CODE:
      return t('promoCode:errorNotApplicable')
    default:
      return t('promoCode:errorGeneric')
  }
}

export const PromoCodeInput: FunctionComponent<Props> = ({ testNameSpace = 'promo-code-input' }) => {
  const { t } = useTranslation()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const checkoutState = useCheckoutState()
  const token = useCheckoutToken()
  const { locale } = useAppRouter()

  const promotion = checkoutState.started?.promotion
  const persistedManualPromo = checkoutState.manualPromoCode
  const checkoutCurrency = checkoutState.started?.consumerAmount?.currency
  const promotionCurrency = promotion?.discount?.currency

  const { formatMoney } = useMoney({ locale, currency: checkoutCurrency ?? promotionCurrency, decimalPlaces: 2 })

  const isAutoApplied = ((): boolean => {
    if (!promotion) return false
    if (!persistedManualPromo) return true
    if (persistedManualPromo.token !== token) return true
    return persistedManualPromo.code !== promotion.code
  })()

  const [updatePromo, { isLoading, reset: resetPromoMutation }] = useUpdateCheckoutPromoMutation({ fixedCacheKey: promoMutationCacheKey(token) })

  useEffect(() => {
    return () => {
      resetPromoMutation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])
  const [attemptCount, setAttemptCount] = useState(0)
  const [liveMessage, setLiveMessage] = useState('')
  const [view, setView] = useState<ViewState>(() => {
    if (promotion) {
      return {
        kind: 'applied',
        promoCode: promotion.code,
        discountAmount: formatMoney(promotion.discount.amount),
      }
    }
    return { kind: 'collapsed' }
  })

  const inputRef = useRef<HTMLInputElement>(null)
  const errorId = useId()
  const accordionId = useId()
  const isAttemptLimitReached = attemptCount >= MAX_ATTEMPTS

  const externalPromoCode = promotion?.code
  const externalDiscountAmount = promotion?.discount?.amount
  useEffect(() => {
    if (externalPromoCode && externalDiscountAmount) {
      setView((prev) => {
        if (prev.kind === 'applied' && prev.promoCode === externalPromoCode) {
          return prev
        }
        return {
          kind: 'applied',
          promoCode: externalPromoCode,
          discountAmount: formatMoney(externalDiscountAmount),
        }
      })
    } else {
      setView((prev) => {
        if (prev.kind === 'applied') {
          return { kind: 'collapsed' }
        }
        return prev
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPromoCode, externalDiscountAmount])

  useEffect(() => {
    if (view.kind === 'expanded') {
      inputRef.current?.focus()
    }
  }, [view.kind])

  const handleToggle = (): void => {
    if (view.kind === 'collapsed') {
      setView({ kind: 'expanded', inputValue: '', errorCode: null })
      logEvent(TrackingEvent.PRESSED_PROMO_CODE_TOGGLE, { action: 'expanded' })
      return
    }
    if (view.kind === 'expanded') {
      setView({ kind: 'collapsed' })
      logEvent(TrackingEvent.PRESSED_PROMO_CODE_TOGGLE, { action: 'collapsed' })
    }
  }

  const handleApply = async (): Promise<void> => {
    if (view.kind !== 'expanded') return
    if (isLoading) return
    if (isAttemptLimitReached) return
    if (view.errorCode) return
    const trimmedCode = view.inputValue.trim().toLowerCase()
    if (!trimmedCode) return

    const nextAttempt = attemptCount + 1
    setAttemptCount(nextAttempt)
    logEvent(TrackingEvent.PRESSED_PROMO_CODE_APPLY, { attemptNumber: nextAttempt })

    try {
      const result = await updatePromo({ token, request: { action: 'APPLY', promoCode: trimmedCode } }).unwrap()
      const applied = result.promotion
      if (applied) {
        const discountAmount = formatMoney(applied.discount.amount)
        logEvent(TrackingEvent.PROMO_CODE_APPLIED, { promoCode: trimmedCode, discountAmount })
        setLiveMessage(t('promoCode:applied'))
        setView({ kind: 'applied', promoCode: applied.code, discountAmount })
      } else {
        logEvent(TrackingEvent.PROMO_CODE_APPLY_FAILED, {
          errorCode: PROMO_SILENT_NO_OP_ERROR_CODE,
          httpStatus: 200,
          attemptNumber: nextAttempt,
        })
        setLiveMessage(resolveErrorMessage(PROMO_SILENT_NO_OP_ERROR_CODE, t))
        setView({ kind: 'expanded', inputValue: view.inputValue, errorCode: PROMO_SILENT_NO_OP_ERROR_CODE })
      }
    } catch (err: unknown) {
      const error = err as { data?: { errorCode?: string }; status?: number }
      const rawErrorCode = error?.data?.errorCode ?? 'UNKNOWN'
      const httpStatus = error?.status
      const resolvedError = httpStatus === 429 ? ErrorCode.RateLimited : rawErrorCode
      logEvent(TrackingEvent.PROMO_CODE_APPLY_FAILED, { errorCode: resolvedError, httpStatus, attemptNumber: nextAttempt })
      setLiveMessage(resolveErrorMessage(resolvedError, t))
      setView({ kind: 'expanded', inputValue: view.inputValue, errorCode: resolvedError })
    }
  }

  const handleInputChange = (e: InputOnChange): void => {
    if (view.kind !== 'expanded') return
    setView({ kind: 'expanded', inputValue: e.target.value, errorCode: null })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (e.key === 'Enter') {
      void handleApply()
    }
  }

  if (isAutoApplied) {
    return null
  }

  const loadingMarker = isLoading ? <span data-testid={`${testNameSpace}-loading`} hidden aria-hidden='true' /> : null

  const expandedContent = view.kind === 'expanded' ? view : null
  const inputValue = expandedContent?.inputValue ?? ''
  const errorCode = expandedContent?.errorCode ?? null
  const hasInput = inputValue.trim().length > 0
  const errorMessage = errorCode ? resolveErrorMessage(errorCode, t) : null
  const isApplyDisabled = !hasInput || isAttemptLimitReached || !!errorCode

  const isAccordionExpanded = view.kind === 'expanded'

  const renderAccordionBody = (): ReactNode => {
    const inputHasError = isAttemptLimitReached || !!errorCode
    const visibleErrorMessage = isAttemptLimitReached ? t('promoCode:tooManyAttempts') : errorMessage
    return (
      <>
        <div className={styles.inputRow}>
          <Input
            ref={inputRef}
            testNameSpace={testNameSpace}
            value={inputValue}
            controlled
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={t('promoCode:placeholder')}
            aria-label={t('promoCode:label')}
            aria-invalid={inputHasError}
            aria-describedby={errorId}
            error={inputHasError}
            disabled={isAttemptLimitReached || isLoading}
            fullWidth
          />
          <Button
            kind='Secondary'
            disabled={isApplyDisabled || isLoading}
            loading={isLoading}
            onClick={() => void handleApply()}
            data-testid={`${testNameSpace}-apply`}
          >
            <Trans i18nKey='promoCode:apply' />
          </Button>
        </div>
        {visibleErrorMessage ? (
          <div className={styles.errorRow} id={errorId} data-testid={`${testNameSpace}-error`} role='alert'>
            <Text size='XS' color='Error' renderAs='span'>
              {visibleErrorMessage}
            </Text>
          </div>
        ) : (
          <Text size='XS' color='Gray40' id={errorId} data-testid={`${testNameSpace}-helper`}>
            <Trans i18nKey='promoCode:helperText' />
          </Text>
        )}
      </>
    )
  }

  return (
    <div className={styles.container} data-testid={testNameSpace}>
      <TrackOnMount eventName={TrackingEvent.VIEWED_PROMO_CODE_INPUT} />
      <div aria-live='polite' aria-atomic='true' className={styles.liveRegion}>
        {liveMessage}
      </div>
      {loadingMarker}
      {view.kind === 'applied' ? (
        <div data-testid={`${testNameSpace}-applied`}>
          <Grid justifyContent='space-between'>
            <GridItem>
              <Text size='M'>
                <Trans i18nKey='promoCode:codeApplied' />
              </Text>
            </GridItem>
            <GridFixedItem align='right'>
              <Text size='M' color='Success' renderAs='span'>
                - {view.discountAmount}
              </Text>
            </GridFixedItem>
          </Grid>
        </div>
      ) : (
        <Accordion id={accordionId} expanded={isAccordionExpanded} onChange={handleToggle} testNameSpace={`${testNameSpace}-toggle`}>
          <Accordion.Button>
            <Text size='M'>
              <Trans i18nKey='promoCode:havePromo' />
            </Text>
          </Accordion.Button>
          <Accordion.Details>{renderAccordionBody()}</Accordion.Details>
        </Accordion>
      )}
    </div>
  )
}
